import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const SOURCES = [
  "Website", "Email", "Phone", "Walk-In", "Referral", "Partner",
  "Distributor", "Exhibition", "Trade Fair", "Campaign", "WhatsApp",
  "Social Media", "LinkedIn", "Facebook", "Instagram", "Google Ads",
  "Customer Reference", "Inbound Call", "Outbound Campaign", "Other",
];

const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const initialForm = {
  enquiryDate: new Date().toISOString().slice(0, 10),
  source: "Website",
  enquiryType: "Product Enquiry",
  campaign: "",
  priority: "High",
  territory: "",
  owner: "",
  companyName: "",
  contactPerson: "",
  designation: "",
  mobile: "",
  email: "",
  website: "",
  industry: "",
  country: "India",
  state: "",
  city: "",
  productInterest: "",
  quantity: "",
  budget: "",
  expectedTimeline: "",
  projectValue: "",
  requirementDescription: "",
};

export default function CreateEnquiry() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
    setMessage("");
  };

  const validate = () => {
    const required = {
      enquiryDate: "Enquiry Date",
      source: "Enquiry Source",
      enquiryType: "Enquiry Type",
      owner: "Owner",
      companyName: "Company Name",
      contactPerson: "Contact Person",
      mobile: "Mobile Number",
      country: "Country",
      requirementDescription: "Requirement Description",
    };

    const nextErrors = {};
    Object.entries(required).forEach(([field, label]) => {
      if (!String(form[field] || "").trim()) {
        nextErrors[field] = `${label} is required`;
      }
    });

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address";
    }

    if (form.mobile && !/^[+()\d\s-]{7,20}$/.test(form.mobile)) {
      nextErrors.mobile = "Enter a valid mobile number";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleAction = (action) => {
    setMessage("");

    if (action !== "cancel" && !validate()) {
      setMessage("Please complete the required fields.");
      return;
    }

    if (action === "cancel") {
      navigate("/elosca-crm/enquiry");
      return;
    }

    setMessage(
      action === "draft"
        ? "Preview only: Save Draft is not connected to the backend yet."
        : "Preview only: Save & Qualify is not connected to the backend yet."
    );
  };

  const fieldClass = (field) => errors[field] ? "elosca-create-field invalid" : "elosca-create-field";

  const input = (field, label, options = {}) => (
    <label className={fieldClass(field)} key={field}>
      <span>{label}{options.required && <b className="required">*</b>}</span>
      {options.options ? (
        <select
          value={form[field]}
          onChange={(e) => update(field, e.target.value)}
        >
          <option value="">Select {label}</option>
          {options.options.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      ) : (
        <input
          type={options.type || "text"}
          value={form[field]}
          onChange={(e) => update(field, e.target.value)}
          placeholder={options.placeholder || ""}
        />
      )}
      {errors[field] && <small>{errors[field]}</small>}
    </label>
  );

  return (
    <div className="elosca-create-page">
      <div className="elosca-create-breadcrumb">
        <button type="button" onClick={() => navigate("/elosca-crm/enquiry")}>Home</button>
        <span>›</span>
        <span>Sales</span>
        <span>›</span>
        <button type="button" onClick={() => navigate("/elosca-crm/enquiry")}>
          Enquiry Management
        </button>
        <span>›</span>
        <strong>Create Enquiry</strong>
      </div>

      <div className="elosca-create-heading">
        <h1>Create Enquiry</h1>
        <div className="elosca-create-actions">
          <button type="button" onClick={() => handleAction("cancel")}>Cancel</button>
          <button type="button" onClick={() => handleAction("draft")}>Save Draft</button>
          <button type="button" className="primary" onClick={() => handleAction("qualify")}>
            Save &amp; Qualify
          </button>
        </div>
      </div>

      {message && <div className="elosca-create-message" role="status">{message}</div>}

      <div className="elosca-create-grid">
        <section className="elosca-create-card">
          <h2>Basic Information</h2>
          {input("enquiryDate", "Enquiry Date", { type: "date", required: true })}
          {input("source", "Enquiry Source", { options: SOURCES, required: true })}
          {input("enquiryType", "Enquiry Type", {
            options: ["Product Enquiry", "Service Enquiry", "General Enquiry"],
            required: true,
          })}
          {input("campaign", "Campaign")}
          {input("priority", "Priority", { options: PRIORITIES })}
          {input("territory", "Territory")}
          {input("owner", "Owner", { required: true, placeholder: "Enter owner name" })}
        </section>

        <section className="elosca-create-card">
          <h2>Customer Information</h2>
          {input("companyName", "Company Name", { required: true })}
          {input("contactPerson", "Contact Person", { required: true })}
          {input("designation", "Designation")}
          {input("mobile", "Mobile Number", { required: true, type: "tel" })}
          {input("email", "Email", { type: "email" })}
          {input("website", "Website", { type: "url" })}
          {input("industry", "Industry")}
          {input("country", "Country", { required: true })}
          {input("state", "State")}
          {input("city", "City")}
        </section>

        <section className="elosca-create-card">
          <h2>Requirement Details</h2>
          {input("productInterest", "Product Interest")}
          {input("quantity", "Expected Quantity", { type: "number" })}
          {input("budget", "Estimated Budget")}
          {input("expectedTimeline", "Expected Timeline", { placeholder: "e.g. 3 months" })}
          {input("projectValue", "Project Value")}
          <label className={fieldClass("requirementDescription")}>
            <span>Requirement Description<b className="required">*</b></span>
            <textarea
              rows={4}
              value={form.requirementDescription}
              onChange={(e) => update("requirementDescription", e.target.value)}
              placeholder="Describe the customer's requirement"
            />
            {errors.requirementDescription && <small>{errors.requirementDescription}</small>}
          </label>
        </section>

        <section className="elosca-create-card elosca-create-ai">
          <h2>Zyra AI Assist</h2>
          <div><span>Duplicate Check</span><strong>Pending validation</strong></div>
          <div><span>Lead Potential</span><strong>Pending analysis</strong></div>
          <div><span>Suggested Territory</span><strong>{form.territory || "Not available"}</strong></div>
          <div><span>Suggested Owner</span><strong>{form.owner || "Not available"}</strong></div>
          <p>AI recommendations will be connected when the relevant service is integrated.</p>
          <button type="button" onClick={() => setMessage("AI insights are not connected in this frontend preview.")}>
            View AI Insights
          </button>
        </section>
      </div>

      <section className="elosca-create-card elosca-create-attachments">
        <h2>Attachments</h2>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
          hidden
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        <div className="elosca-create-upload">
          <span>Drag and drop files here or</span>
          <button type="button" onClick={() => fileInputRef.current?.click()}>
            Browse Files
          </button>
        </div>
        <small>Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, ZIP.</small>
        {files.length > 0 && (
          <ul className="elosca-create-file-list">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`}>{file.name}</li>
            ))}
          </ul>
        )}
      </section>

      <div className="elosca-create-bottom-actions">
        <button type="button" onClick={() => handleAction("cancel")}>Cancel</button>
        <button type="button" onClick={() => handleAction("draft")}>Save Draft</button>
        <button type="button" className="primary" onClick={() => handleAction("qualify")}>
          Save &amp; Qualify
        </button>
      </div>
    </div>
  );
}